import { clsx } from 'clsx';
import type * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import type { NotificationMetadata } from '@gitbook/app-notifications';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    DateShortRelative,
    ListItem,
    ListItemBody,
    ListItemDescription,
    ListItemLeading,
    ListItemTrailing,
} from '@gitbook/spine-react';

import { NotificationFormattedTitle } from './NotificationFormattedTitle';
import { NotificationIcon } from './NotificationIcon';
import type { NotificationData } from './notifications';
import { useOpenNotification } from './useOpenNotification';

/**
 * Renders a notification row.
 */
export function NotificationItem(props: { notification: NotificationData }): React.ReactElement {
    const { notification } = props;
    const openNotification = useOpenNotification();

    const handleMarkAsRead = async () => {
        analytics.logEvent(
            analytics.createNotificationEvent(notification, {
                type: 'notification_mark_as_read',
            })
        );

        await notification.markAsRead();
    };

    const handleMarkAsUnread = async () => {
        analytics.logEvent(
            analytics.createNotificationEvent(notification, {
                type: 'notification_mark_as_unread',
            })
        );

        await notification.markAsUnread();
    };

    const metadata = notification.customAttributes as NotificationMetadata | null;

    return (
        <ListItem
            kind="bordered"
            size="large"
            className={clsx(
                'group/notification-item',
                notification.isRead ? 'opacity-70' : 'bg-[var(--neutral-2)]'
            )}
            onPress={() => {
                openNotification(notification);
            }}
        >
            <div className="flex w-full flex-1 items-start justify-between gap-3">
                <div className="flex flex-1 items-center">
                    <ListItemLeading>
                        <NotificationIcon metadata={metadata} />
                    </ListItemLeading>

                    <ListItemBody className="max-w-[80%]">
                        <ListItemDescription>
                            <NotificationFormattedTitle notification={notification} />
                        </ListItemDescription>
                    </ListItemBody>
                </div>

                <div className="-mr-[5px] flex flex-col gap-0.5">
                    <ListItemTrailing when="always" className="self-end">
                        <Button
                            iconOnly
                            kind="blank"
                            size="small"
                            leadingIcon={notification.isRead ? Icon.Read : Icon.Unread}
                            label={`Mark as ${notification.isRead ? 'unread' : 'read'}`}
                            onPress={notification.isRead ? handleMarkAsUnread : handleMarkAsRead}
                        />
                    </ListItemTrailing>
                    <DateShortRelative
                        value={notification.sentAt || new Date()}
                        onlyDate={false}
                        className="self-end pr-[5px] text-ui-small text-muted"
                    />
                </div>
            </div>
        </ListItem>
    );
}
